import {JSONContent} from '../types';
import {isVimeoVideo, isYouTubeVideo} from '../utils';
import {VimeoEmbed} from './VimeoEmbed';
import {YouTubeEmbed} from './YouTubeEmbed';
import {AmenitiesList, Divider, TwoColumnLayout} from '../styled';
import {Heading} from './Heading';
import {Paragraph} from './Paragraph';
import {Amenity} from './Amenity';
import {MapBlockView} from './MapEmbed';
import {File} from './File';
import {ImageItem} from './ImageItem';

type Props = {
  node: JSONContent;
};
const ContentItemFactory = ({node}: Props) => {
  if (!node) return null;

  switch (node.type) {
    case 'heading': {
      const level = node.attrs?.level;
      return <Heading level={level} content={node.content ?? []} attrs={node.attrs} />;
    }

    case 'paragraph': {
      const level = node.attrs?.level;
      return <Paragraph level={level} content={node.content ?? []} attrs={node.attrs} />;
    }

    case 'horizontalRule': {
      return <Divider />;
    }

    case 'videoBlock': {
      if (isYouTubeVideo(node.attrs?.url)) {
        return <YouTubeEmbed url={node.attrs?.url} />;
      }
      if (isVimeoVideo(node.attrs?.url)) {
        return <VimeoEmbed url={node.attrs?.url} />;
      }
      return null;
    }

    case 'amenities': {
      return (
        <AmenitiesList>
          {node.content?.map((amenity, index) => (
            <Amenity key={index} icon={amenity.attrs?.icon} name={amenity.attrs?.name} />
          ))}
        </AmenitiesList>
      );
    }

    case 'mapBlock': {
      return <MapBlockView url={node.attrs?.url} />;
    }

    case 'fileUpload': {
      return <File name={node.attrs?.name} url={node.attrs?.src} />;
    }

    case 'imageBlock': {
      return <ImageItem src={node.attrs?.src} attrs={node.attrs ?? {}} />;
    }

    case 'columns': {
      if (node.attrs?.layout) {
        return (
          <TwoColumnLayout className={`layout-two-column ${node.attrs?.layout}`}>
            {node.content?.map((column, index) => (
              <ContentItemFactory key={index} node={column} />
            ))}
          </TwoColumnLayout>
        );
      }
      return null;
    }

    case 'column': {
      return (
        <div data-position={node.attrs?.position}>
          {node.content?.map((child, index) => (
            <ContentItemFactory key={index} node={child} />
          ))}
        </div>
      );
    }

    default:
      return null;
  }
};

const GuidebookContent = ({content}: {content?: JSONContent[]}) => {
  return (
    <div>
      {content?.map((node, index) => (
        <ContentItemFactory key={index} node={node} />
      ))}
    </div>
  );
};

export {GuidebookContent};
